import { useCallback, useMemo } from 'react';

import { type User, useUserData } from '@givebacks/api/core';
import type { Merchant } from '@givebacks/api/offers';

import usePlatformService from '../../../hooks/usePlatformService';

const WILDFIRE_DEVICE_ID = '10270859';

type CarteraMerchant = Omit<Merchant, 'wildfire_id'> & { click_url: string };
type WildFireMerchant = Omit<Merchant, 'click_url'> & { wildfire_id: string };

/** @description Checks if the merchant is a WildFire merchant */
const merchantIsWildFire = (merchant: Merchant): merchant is WildFireMerchant => {
  return 'wildfire_id' in merchant && Boolean(merchant.wildfire_id);
};

/** @description Checks if the merchant is a Cartera merchant */
const merchantIsCartera = (
  merchant: Omit<Merchant, 'wildfire_id'>
): merchant is CarteraMerchant => {
  return 'click_url' in merchant && Boolean(merchant.click_url);
};

/** @description Gets the user info from the user */
const getUserInfo = (user: User) => {
  return {
    uuid: user.uuid,
    cartera_member_id: user.cartera_member_id,
  };
};

/** @description Adds the user info to the Cartera link */
const addUserInfoToCarteraLink = (link: string, userInfo?: ReturnType<typeof getUserInfo>) => {
  try {
    if (!userInfo) {
      return link;
    }
    const carteraLink = new URL(link);
    if (userInfo.cartera_member_id) {
      carteraLink.searchParams.set('mem_id', userInfo.cartera_member_id);
    }
    return carteraLink.toString();
  } catch {
    return link;
  }
};

/** @description Returns the affiliate link for the merchant */
const useAffiliateLink = (merchant: null | Merchant) => {
  const { data: userInfo } = useUserData(getUserInfo);
  const Platform = usePlatformService();

  const getWildFireUrl = useCallback(
    (merchant: Omit<Merchant, 'click_url'>) => {
      if (!userInfo) {
        return merchant.url;
      }
      const protocol = Platform.mobileBrowser == 'ios' ? 'http' : 'https';
      return `${protocol}://wild.link/e?c=${merchant.wildfire_id}&d=${WILDFIRE_DEVICE_ID}&tc=${userInfo.uuid}`;
    },
    [Platform.mobileBrowser, userInfo]
  );

  const buildCarteraUrl = useCallback(
    (merchant: CarteraMerchant) => {
      return addUserInfoToCarteraLink(merchant.click_url, userInfo);
    },
    [userInfo]
  );

  const url = useMemo(() => {
    if (!merchant) {
      return undefined;
    }

    if (merchantIsCartera(merchant)) {
      return buildCarteraUrl(merchant);
    }
    if (merchantIsWildFire(merchant)) {
      return getWildFireUrl(merchant);
    }
    return merchant.url;
  }, [buildCarteraUrl, getWildFireUrl, merchant]);

  const navigate = useCallback(() => {
    window.open(url, '_blank', '');
  }, [url]);

  return { url, navigate };
};

export { useAffiliateLink };
